import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { SavedFilterType } from 'shared';

import { templateGroupSlugs } from '../components/Settings/EmailTemplates/constants';
import { OrgSavedFilterType } from '../components/Navigation/SavedFiltersNav';

// Pages with submenu items shown in the nested sidebar: the invoice/contact
// tables (saved + quick filters), the email templates list (template
// types) and the group pages (the org's groups).
export type NestedNavType = OrgSavedFilterType | `emailTemplates` | `groups`;

// Layout state for the desktop sidebar. Pages with submenu items (invoice /
// contact tables) get a nested sidebar next to an icon-only main sidebar,
// Intercom style. The user can also collapse/expand the main sidebar
// anywhere; that choice is persisted. `null` means no explicit choice yet, in
// which case the sidebar collapses only on pages with a nested sidebar.
export function useSidebarLayout() {
  const { pathname } = useLocation();
  const [userCollapsed, setUserCollapsed] = useLocalStorage<boolean | null>(`mainSidebarCollapsed`, null);

  const nestedType = useMemo<NestedNavType | null>(() => {
    const parts = pathname.split(`/`).filter(Boolean);

    if (parts.length === 2 && parts[1] === `invoices`) {
      return SavedFilterType.Invoice;
    }

    if (parts.length === 2 && parts[1] === `escalations`) {
      return SavedFilterType.Escalation;
    }

    if (parts.length === 2 && parts[1] === `actions`) {
      return SavedFilterType.Action;
    }

    if (parts.length === 3 && parts[1] === `contacts` && parts[2] === `manage`) {
      return SavedFilterType.Contact;
    }

    // Any group page: /:orgId/groups/:groupId[/...]
    if (parts.length >= 3 && parts[1] === `groups`) {
      return `groups`;
    }

    if (parts[1] === `settings` && parts[2] === `email-templates`) {
      // List page, or a type filter slug — but not the :templateId detail page
      if (parts.length === 3 || (parts.length === 4 && Object.values(templateGroupSlugs).includes(parts[3]))) {
        return `emailTemplates`;
      }
    }

    return null;
  }, [pathname]);

  const hasNested = nestedType !== null;
  const collapsed = userCollapsed ?? hasNested;

  // Main sidebar is 16rem expanded / 4rem collapsed. The nested panel sits
  // inside the content column (below the top nav) so it doesn't affect this.
  const contentMarginClass = collapsed ? `md:ml-16` : `md:ml-64`;

  return {
    nestedType,
    hasNested,
    collapsed,
    setCollapsed: setUserCollapsed,
    toggleCollapsed: () => setUserCollapsed(!collapsed),
    contentMarginClass,
  };
}
